import {
  Box,
  ButtonBase,
  CircularProgress,
  Dialog,
  Stack,
  Typography,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";

const BRAND_BLUE = "#1560D4";

const OPTIONS = [
  {
    action: "download",
    en: "Download",
    th: "ดาวน์โหลด",
    icon: <FileDownloadOutlinedIcon />,
  },
  {
    action: "print",
    en: "Print",
    th: "พิมพ์",
    icon: <PrintOutlinedIcon />,
  },
];

// A tappable card, laid out as a row on phones and as a centred tile from `sm` up
const OutputOption = ({ option, isLoading, disabled, onSelect }) => (
  <ButtonBase
    onClick={() => onSelect(option.action)}
    disabled={disabled}
    sx={{
      display: "flex",
      flexDirection: { xs: "row", sm: "column" },
      justifyContent: { xs: "flex-start", sm: "center" },
      gap: "12px",
      p: { xs: "12px 16px", sm: "24px 16px" },
      border: "1px solid #E6E6E6",
      borderRadius: "8px",
      textAlign: { xs: "left", sm: "center" },
      transition: "border-color 0.2s, background-color 0.2s",
      "&:hover": { borderColor: BRAND_BLUE, bgcolor: "#F5F9FF" },
      "&.Mui-focusVisible": { borderColor: BRAND_BLUE },
      // The option being exported stays fully visible, the other one fades
      "&.Mui-disabled": { opacity: isLoading ? 1 : 0.5 },
    }}
  >
    <Box
      sx={{
        width: 48,
        height: 48,
        flexShrink: 0,
        borderRadius: "50%",
        bgcolor: "#E8F0FA",
        color: BRAND_BLUE,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {isLoading ? <CircularProgress size={22} color="inherit" /> : option.icon}
    </Box>
    <Stack>
      <Typography fontSize={15} fontWeight={600}>
        {option.en}
      </Typography>
      <Typography fontSize={13} color="text.secondary">
        {option.th}
      </Typography>
    </Stack>
  </ButtonBase>
);

// Asks whether the PDF of a just generated FTI (or reprint) should be downloaded or sent straight
// to the printer. A choice is required, so there's no cancel and no closing on Esc / backdrop.
// `exportingAction` is the choice being processed ("download" | "print" | null)
export const FtiOutputDialog = ({ open, exportingAction = null, onSelect }) => {
  const isExporting = Boolean(exportingAction);

  return (
    <Dialog
      open={open}
      PaperProps={{
        sx: {
          width: "440px",
          maxWidth: "calc(100% - 32px)",
          m: "16px",
          p: { xs: "16px", sm: "24px" },
          borderRadius: "12px",
        },
      }}
    >
      <Stack gap="20px">
        <Stack gap="4px">
          <Typography fontSize={18} fontWeight={600} lineHeight={1.3}>
            Download or Print?
          </Typography>
          <Typography fontSize={15} fontWeight={500} color="text.secondary">
            ดาวน์โหลดหรือพิมพ์?
          </Typography>
          <Typography fontSize={13} color="text.secondary" mt="4px">
            Choose how you want the FTI PDF/
            เลือกวิธีรับไฟล์ PDF ใบกำกับภาษีเต็มรูป
          </Typography>
        </Stack>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: "12px",
          }}
        >
          {OPTIONS.map((option) => (
            <OutputOption
              key={option.action}
              option={option}
              isLoading={exportingAction === option.action}
              disabled={isExporting}
              onSelect={onSelect}
            />
          ))}
        </Box>
      </Stack>
    </Dialog>
  );
};
